import depsloader from "../../../depsloader";

const GEOCODER_SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const GEOCODER_REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT_MS = 25000;

function isFiniteNumber(value) {
  return typeof value === "number" && isFinite(value);
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this._seq = 0;
  }

  // Maps-Key wird serverseitig geholt und via html-dataset ans Frontend gereicht.
  // Er wird nie im JavaScript rekonstruiert und darf fehlen.
  _mapsKey() {
    try {
      const el = document.documentElement;
      const key = el && el.dataset ? el.dataset.mapsGeocoderKey : "";
      return typeof key === "string" ? key.trim() : "";
    } catch (e) {
      return "";
    }
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      try {
        this.axiosRequest.source.cancel();
      } catch (e) {
        // bereits beendet – ignorieren
      }
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Adapter für die api.screenwork.de-Geocoder-Antwort.
  // Wertet properties.type und district vorrangig aus, behandelt alle bekannten
  // Antworttypen (house, street, city, district, locality) und greift erst danach
  // auf osm_key/osm_value als Legacy-Fallback zurück. Das Feature wird nicht mutiert.
  _geoaddress(feature) {
    if (!feature || !feature.properties || !feature.geometry) {
      return null;
    }

    const p = feature.properties;
    const coords = feature.geometry.coordinates;
    if (
      !Array.isArray(coords) ||
      coords.length < 2 ||
      !isFiniteNumber(coords[0]) ||
      !isFiniteNumber(coords[1])
    ) {
      return null;
    }

    const geo = {};
    geo.lng = coords[0];
    geo.lat = coords[1];

    if (p.state) {
      geo.state = p.state;
    }
    if (p.country) {
      geo.country = p.country;
    }

    const type = typeof p.type === "string" ? p.type : null;
    const isLegacyHighway = p.osm_key === "highway";

    // Straße und Hausnummer
    if (p.street) {
      geo.street = p.street;
      if (p.housenumber) {
        geo.number = p.housenumber;
      }
    } else if (!p.city && p.name && (type === "street" || type === "house" || isLegacyHighway)) {
      geo.street = p.name;
    }

    // Postleitzahl
    if (p.postcode) {
      geo.zip = p.postcode;
    }

    // Ort und Ortsteil
    if (p.city) {
      geo.city = p.city;
      if (p.district && p.district !== p.city) {
        geo.district = p.district;
      } else if (
        !p.district &&
        p.name &&
        p.name !== p.city &&
        (p.osm_key === "boundary" || (p.osm_key === "place" && p.osm_value === "suburb"))
      ) {
        geo.district = p.name;
      }
    } else if (type === "city" || type === "locality") {
      if (p.name) {
        geo.city = p.name;
      }
    } else if (type === "district") {
      if (p.district) {
        geo.district = p.district;
      } else if (p.name) {
        geo.district = p.name;
      }
    } else if (p.district) {
      geo.district = p.district;
    }

    geo.text = this._geoaddressText(geo);
    // Fehlt jede Orts-/Straßenangabe, den Feature-Namen als Beschriftung erhalten,
    // ohne ihn als Stadt zu erfinden.
    if (!geo.text && p.name) {
      geo.text = p.name;
    }

    return geo;
  }

  geocode(params) {
    const key = this._mapsKey();
    if (!key) {
      // Ohne Key kontrolliert ohne Anfrage enden.
      return Promise.resolve([]);
    }

    // Laufende Anfrage zuverlässig beenden, bevor eine neue startet.
    this.cancelAutocomplete();

    const CancelToken = this.axios.CancelToken;
    const source = CancelToken.source();
    this.axiosRequest = { source };
    const seq = ++this._seq;

    const requestParams = Object.assign({}, params, { key: key, lang: params && params.lang ? params.lang : "de" });

    return this.axios
      .get(GEOCODER_SEARCH_URL, {
        params: requestParams,
        cancelToken: source.token,
        timeout: REQUEST_TIMEOUT_MS
      })
      .then(res => {
        // Nur die jüngste Antwort darf Treffer verändern.
        if (seq !== this._seq) {
          return [];
        }
        const results = [];
        const features = res && res.data ? res.data.features : null;
        if (Array.isArray(features)) {
          features.forEach(feature => {
            const geoaddress = this._geoaddress(feature);
            if (geoaddress) {
              results.push(geoaddress);
            }
          });
        }
        return results;
      })
      .catch(thrown => {
        if (this.axios.isCancel && this.axios.isCancel(thrown)) {
          return [];
        }
        console.warn("Geocoding fehlgeschlagen", thrown);
        return [];
      })
      .then(results => {
        if (this.axiosRequest && this.axiosRequest.source === source) {
          this.axiosRequest = null;
        }
        return results;
      });
  }

  autocomplete(q) {
    const params = {
      q: q,
      lang: "de",
      limit: 10
    };

    return this.geocode(params);
  }

  reverse(lat, lng) {
    const key = this._mapsKey();
    const latValid = isFiniteNumber(lat) && lat >= -90 && lat <= 90;
    const lonValid = isFiniteNumber(lng) && lng >= -180 && lng <= 180;

    if (!key || !latValid || !lonValid) {
      // Ohne Key oder ungültige Koordinaten kontrolliert ohne Anfrage enden.
      return Promise.resolve(null);
    }

    return this.axios
      .get(GEOCODER_REVERSE_URL, {
        params: {
          lat: lat,
          lon: lng,
          lang: "de",
          key: key
        },
        timeout: REQUEST_TIMEOUT_MS
      })
      .then(res => {
        const features = res && res.data ? res.data.features : null;
        if (Array.isArray(features) && features.length > 0) {
          return this._geoaddress(features[0]);
        }
        return null;
      })
      .catch(err => {
        console.warn("Reverse-Geocoding fehlgeschlagen", err);
        return null;
      });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    });
  });
}

export { GeocoderFactory };

export default {};
